import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "../components/AuthContext";
import {
  addDoc,
  collection,
  Timestamp,
  doc,
  updateDoc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { getNextInvoiceNumber } from "../services/invoiceService";
import DeleteIcon from "@mui/icons-material/Delete";

const CreateInvoice = () => {
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const { id } = useParams();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [docNumber, setDocNumber] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [toAddress, setToAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([
    { desc: "", hsn: "", unit: "pcs", qty: 1, rate: 0 },
  ]);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoiceDocId, setInvoiceDocId] = useState(null);
  const [gstPercent, setGstPercent] = useState(18);
  const [toAddressList, setToAddressList] = useState([]);
  const [showSignModal, setShowSignModal] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(true);
  const { user } = useAuth();

  /* ---------- LOAD COMPANIES ---------- */
  useEffect(() => {
    if (!user) return;

    const fetchCompanies = async () => {
      try {
        const snap = await getDocs(
          collection(db, "users", user.uid, "companies"),
        );

        setCompanies(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          })),
        );
      } catch (err) {
        console.error("Failed to load companies:", err);
      }
    };

    fetchCompanies();
  }, [user]);

  /* ---------- LOAD TO ADDRESS LIST ---------- */
  useEffect(() => {
    if (!user) return;

    const fetchToAddresses = async () => {
      try {
        const snap = await getDocs(
          collection(db, "users", user.uid, "toAddresses"),
        );

        setToAddressList(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          })),
        );
      } catch (err) {
        console.error(err);
      }
    };

    fetchToAddresses();
  }, [user]);

  /* ---------- LOAD INVOICE (EDIT MODE) ---------- */
  useEffect(() => {
    if (!id || !user) return;

    const fetchInvoice = async () => {
      const snap = await getDoc(doc(db, "users", user.uid, "invoices", id));
      if (!snap.exists()) return;

      const data = snap.data();

      setInvoiceDocId(id);
      setDocNumber(data.invoiceNumber);
      setFromAddress(data.fromAddress);
      setToAddress(data.toAddress);
      setItems(data.items);
      setNotes(data.notes);
      setGstPercent(data.gstPercent ?? 18);
      try {
        const companySnap = await getDoc(
          doc(db, "users", user.uid, "companies", data.companyId),
        );

        if (companySnap.exists()) {
          setSelectedCompany({
            id: companySnap.id,
            ...companySnap.data(),
          });
        }
      } catch (err) {
        console.error("Failed to load company", err);
      }

      setShowPreview(true);
    };

    fetchInvoice();
  }, [id, user]);

  const total = items.reduce(
    (s, i) => s + Number(i.qty || 0) * Number(i.rate || 0),
    0,
  );

  const gstAmount = (total * gstPercent) / 100;
  const grandTotal = total + gstAmount;

  /* ---------- ITEM HANDLERS ---------- */
  const handleItemChange = (i, field, value) => {
    const updated = [...items];

    if (field === "qty") {
      updated[i][field] = Math.max(1, Number(value));
    } else if (field === "rate") {
      updated[i][field] = Math.max(0, Number(value));
    } else if (field === "unit") {
      updated[i][field] = value;
    } else {
      updated[i][field] = value;
    }

    setItems(updated);
  };

  const addItem = () =>
    setItems([...items, { desc: "", hsn: "", unit: "pcs", qty: 1, rate: 0 }]);

  const removeItem = (i) => {
    if (items.length === 1) return;
    setItems(items.filter((_, index) => index !== i));
  };

  /* ---------- SAVE + PREVIEW ---------- */
  const handleCreateInvoice = async () => {
    if (!user) {
      alert("User not authenticated");
      return;
    }

    if (!selectedCompany || !toAddress) {
      alert("Please select company and enter To Address");
      return;
    }

    setSaving(true);

    try {
      // 🔥 user invoices path
      const invoicesRef = collection(db, "users", user.uid, "invoices");

      if (!invoiceDocId) {
        const invoiceNo = await getNextInvoiceNumber();
        setDocNumber(invoiceNo);

        const docRef = await addDoc(invoicesRef, {
          invoiceNumber: invoiceNo,
          companyId: selectedCompany.id,
          companyName: selectedCompany.CompanyName,
          fromAddress,
          toAddress,
          items,
          total,
          gstPercent,
          gstAmount,
          grandTotal,
          notes,
          status: "DRAFT",
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });

        setInvoiceDocId(docRef.id);
      } else {
        // 🔥 update inside user scope
        await updateDoc(doc(db, "users", user.uid, "invoices", invoiceDocId), {
          fromAddress,
          toAddress,
          items,
          gstAmount,
          gstPercent,
          grandTotal,
          total,
          notes,
          updatedAt: Timestamp.now(),
        });
      }

      setShowPreview(true);
      setTimeout(
        () => previewRef.current?.scrollIntoView({ behavior: "smooth" }),
        100,
      );
    } catch (err) {
      console.error(err);
      alert("Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (num) => {
    return Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /* ---------- PDF ---------- */
  const generatePDF = async (withSignature = true) => {
    await handleCreateInvoice();

    const pdf = new jsPDF("p", "mm", "a4");
    let y = 10;

    /* ================= COMMON STYLES ================= */
    pdf.setDrawColor(0);
    pdf.setLineWidth(0.2);

    /* ================= HEADER ================= */
    pdf.setFillColor(245, 245, 245);
    pdf.rect(5, y, 200, 30, "F"); // background
    pdf.rect(5, y, 200, 30); // border

    pdf.setFont("times", "bold");
    pdf.setFontSize(10);
    pdf.text(`GST: ${selectedCompany.GSTNumber}`, 7, y + 5);
    pdf.text(`Mobile: ${selectedCompany.Mobile}`, 203, y + 5, {
      align: "right",
    });

    pdf.setFontSize(16);
    pdf.text(selectedCompany.CompanyName.toUpperCase(), 105, y + 12, {
      align: "center",
    });

    pdf.setFont("times", "normal");
    pdf.setFontSize(9);
    const addressLines = pdf.splitTextToSize(selectedCompany.Address, 180);
    pdf.text(addressLines, 105, y + 18, { align: "center" });

    pdf.setFont("times", "bold");
    pdf.setFontSize(11);
    pdf.text("INVOICE", 105, y + 28, { align: "center" });

    y += 30;

    /* ================= INVOICE INFO ================= */
    pdf.rect(5, y, 200, 12);

    pdf.setFont("times", "normal");
    pdf.setFontSize(9);
    pdf.text(`Invoice No: ${docNumber}`, 8, y + 7);
    pdf.text(`Date: ${new Date().toLocaleDateString()}`, 203, y + 7, {
      align: "right",
    });

    y += 12;

    /* ================= FROM / TO ================= */
    pdf.rect(5, y, 100, 35);
    pdf.rect(105, y, 100, 35);

    pdf.setFont("times", "bold");
    pdf.setFontSize(10);

    pdf.text("Details of Receiver (Billed To)", 7, y + 5);

    pdf.text("Details of Consignee (Shipped To)", 107, y + 5);

    pdf.setFont("times", "normal");
    pdf.setFontSize(9);

    const splitFrom = pdf.splitTextToSize(fromAddress, 90);
    const splitTo = pdf.splitTextToSize(toAddress, 90);

    pdf.text(splitFrom, 7, y + 11);
    pdf.text(splitTo, 107, y + 11);

    y += 35;

    /* ================= ITEMS TABLE ================= */

    const tableColumn = [
      "S.No",
      "Description of Goods",
      "HSN Code",
      "Unit",
      "Qty",
      "Rate",
      "Amount",
    ];

    const tableRows = items.map((item, index) => {
      const amount = item.qty * item.rate;
      return [
        index + 1,
        item.desc || "-",
        item.hsn || "-",
        item.unit,
        item.qty ?? 0,
        item.rate.toFixed(2),
        amount.toFixed(2),
      ];
    });

    const tableStartY = y;

    autoTable(pdf, {
      startY: y,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",

      margin: { left: 5, right: 5 },

      styles: {
        font: "times",
        fontSize: 9,
        cellPadding: 3,
        lineWidth: 0.2,
      },

      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.2,
      },

      bodyStyles: {
        lineWidth: 0.2,
      },

      tableLineWidth: 0.2,
      tableLineColor: 0,

      columnStyles: {
        0: { halign: "center", cellWidth: 14 },
        1: { halign: "left", cellWidth: 70 },
        2: { halign: "center", cellWidth: 26 },
        3: { halign: "center", cellWidth: 18 },
        4: { halign: "right", cellWidth: 18 },
        5: { halign: "right", cellWidth: 24 },
        6: { halign: "right", cellWidth: 30 },
      },
    });

    const tableEndY = pdf.lastAutoTable.finalY;

    // outer border for table
    pdf.rect(5, tableStartY, 200, tableEndY - tableStartY);

    y = tableEndY;

    /* ================= AMOUNT + TOTAL ================= */
    pdf.rect(5, y, 120, 30);
    pdf.rect(125, y, 80, 30);

    pdf.setFont("times", "bold");
    pdf.setFontSize(10);
    pdf.text("Amount in Words", 7, y + 5);

    pdf.setFont("times", "normal");
    pdf.setFontSize(9);
    const words = pdf.splitTextToSize(amountInWords, 110);
    pdf.text(words, 7, y + 11);

    pdf.text(`Total Taxable Value`, 128, y + 6);
    pdf.text(`${formatCurrency(total)}`, 203, y + 6, { align: "right" });

    pdf.text(`GST @ ${gstPercent}%`, 128, y + 14);
    pdf.text(`${formatCurrency(gstAmount)}`, 203, y + 14, {
      align: "right",
    });

    pdf.setFont("times", "bold");
    pdf.text(`Total Invoice Value`, 128, y + 24);
    pdf.text(`Rs. ${formatCurrency(grandTotal)}`, 203, y + 24, {
      align: "right",
    });

    y += 30;

    /* ================= BANK + SIGNATURE ================= */
    pdf.rect(5, y, 120, 30);
    pdf.rect(125, y, 80, 30);

    pdf.setFont("times", "bold");
    pdf.setFontSize(10);
    pdf.text("Bank Details", 7, y + 6);

    pdf.setFont("times", "normal");
    pdf.setFontSize(9);
    pdf.text(`Bank Name: ${selectedCompany.BankName || "-"}`, 7, y + 14);
    pdf.text(`A/C No: ${selectedCompany.AccountNumber || "-"}`, 7, y + 20);
    pdf.text(`IFSC: ${selectedCompany.IFSC || "-"}`, 7, y + 26);

    pdf.setFont("times", "bold");
    pdf.text(`For ${selectedCompany.CompanyName}`, 165, y + 8, {
      align: "center",
    });

    if (withSignature && selectedCompany?.signatureBase64) {
      pdf.addImage(selectedCompany.signatureBase64, "PNG", 150, y + 10, 30, 12);
    }

    pdf.setFontSize(9);
    pdf.text("Authorised Signatory", 165, y + 26, {
      align: "center",
    });

    /* ================= SAVE ================= */
    await updateDoc(doc(db, "users", user.uid, "invoices", invoiceDocId), {
      status: "FINAL",
      updatedAt: Timestamp.now(),
    });

    pdf.save(`${docNumber}.pdf`);
    navigate("/home");
  };

  const numberToWords = (num) => {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    if (num === 0) return "Zero";

    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
      if (n < 1000)
        return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
      if (n < 100000)
        return inWords(Math.floor(n / 1000)) + " Thousand " + inWords(n % 1000);
      if (n < 10000000)
        return inWords(Math.floor(n / 100000)) + " Lakh " + inWords(n % 100000);
      return "";
    };

    return inWords(Math.floor(num)) + " Rupees Only";
  };

  const amountInWords = numberToWords(grandTotal);

  const UNIT_OPTIONS = [
    // Weight
    { label: "Kilograms (kg)", value: "kg" },
    { label: "Grams (g)", value: "g" },
    { label: "Tons", value: "ton" },

    // Volume
    { label: "Liters (L)", value: "LTR" },
    { label: "Milliliters (mL)", value: "mL" },
    { label: "Gallons", value: "gallon" },

    // Quantity
    { label: "Pieces (pcs)", value: "pcs" },
    { label: "Units", value: "unit" },
    { label: "Dozens", value: "dozen" },
    { label: "Boxes", value: "box" },
    { label: "Cartons", value: "carton" },

    // Length
    { label: "Meters (m)", value: "m" },
    { label: "Centimeters (cm)", value: "cm" },
    { label: "Feet", value: "ft" },

    // Area
    { label: "Square meters (m²)", value: "m2" },
    { label: "Square feet", value: "ft2" },
  ];

  return (
    <div style={styles.page}>
      <button style={styles.backBtn} onClick={() => navigate("/home")}>
        ← Back
      </button>

      <h2 style={styles.title}>Create Invoice</h2>

      <div style={styles.form}>
        {/* COMPANY SELECT */}
        <label style={styles.label}>Select Company</label>
        <select
          style={styles.input}
          value={selectedCompany?.id || ""}
          onChange={(e) => {
            const company = companies.find((c) => c.id === e.target.value);
            setSelectedCompany(company);
            setFromAddress(company?.Address || "");
          }}
        >
          <option value="">Select Company</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.CompanyName}
            </option>
          ))}
        </select>

        <textarea
          style={styles.textarea}
          placeholder="From Address"
          value={fromAddress}
          onChange={(e) => setFromAddress(e.target.value)}
        />
        {/* TO ADDRESS DROPDOWN */}
        <label style={styles.label}>Select To Address</label>
        <select
          style={styles.input}
          onChange={(e) => {
            const selected = toAddressList.find((a) => a.id === e.target.value);
            setToAddress(selected?.address || "");
          }}
        >
          <option value="">Select Saved Address</option>
          {toAddressList.map((addr) => (
            <option key={addr.id} value={addr.id}>
              {addr.address.substring(0, 60)}...
            </option>
          ))}
        </select>

        {/* TO ADDRESS TEXTAREA */}
        <textarea
          style={styles.textarea}
          placeholder="To Address"
          value={toAddress}
          onChange={(e) => setToAddress(e.target.value)}
        />

        <h4>Items</h4>

        <div style={styles.itemsHeader}>
          <span style={styles.itemLabel}>Description</span>
          <span style={styles.itemLabel}>HSN Code</span>
          <span style={styles.itemLabel}>Accounting Unit</span>
          <span style={styles.itemLabel}>Quantity</span>
          <span style={styles.itemLabel}>Unit Price</span>
          <span style={styles.itemLabel}>Amount</span>
          <span style={{ width: 30 }}></span>
        </div>

        {items.map((item, i) => (
          <div key={i} style={styles.itemsRow}>
            <input
              style={styles.itemInput}
              value={item.desc}
              placeholder="Description"
              onChange={(e) => handleItemChange(i, "desc", e.target.value)}
            />

            <input
              style={styles.itemInput}
              value={item.hsn}
              placeholder="HSN Code"
              onChange={(e) => handleItemChange(i, "hsn", e.target.value)}
            />

            <select
              style={styles.itemInput}
              value={item.unit}
              onChange={(e) => handleItemChange(i, "unit", e.target.value)}
            >
              {UNIT_OPTIONS.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>

            <input
              type="number"
              min={1}
              style={styles.itemInput}
              value={item.qty}
              placeholder="Qty"
              onChange={(e) =>
                handleItemChange(i, "qty", Math.max(1, Number(e.target.value)))
              }
            />

            <input
              type="number"
              min={0}
              style={styles.itemInput}
              value={item.rate}
              placeholder="Rate"
              onChange={(e) =>
                handleItemChange(i, "rate", Math.max(0, Number(e.target.value)))
              }
            />

            {/* Amount (readonly calculated) */}
            <input
              style={styles.itemInput}
              value={(item.qty * item.rate).toFixed(2)}
              placeholder="Amount"
              readOnly
            />

            <DeleteIcon
              style={styles.deleteBtn}
              onClick={() => removeItem(i)}
            />
          </div>
        ))}

        <button style={styles.addBtn} onClick={addItem}>
          + Add Item
        </button>
        <label style={styles.label}>GST Percentage (%)</label>
        <input
          type="number"
          style={styles.input}
          value={gstPercent}
          onChange={(e) => setGstPercent(Number(e.target.value))}
          placeholder="Enter GST %"
        />

        <div style={styles.summaryBox}>
          <div style={styles.summaryRow}>
            <span>Sub Total</span>
            <span>₹ {total.toFixed(2)}</span>
          </div>

          <div style={styles.summaryRow}>
            <span>GST ({gstPercent}%)</span>
            <span>₹ {gstAmount.toFixed(2)}</span>
          </div>

          <div style={styles.summaryTotal}>
            <strong>Grand Total</strong>
            <strong>₹ {grandTotal.toFixed(2)}</strong>
          </div>

          <p style={styles.amountWords}>
            Amount in Words: <strong>{amountInWords}</strong>
          </p>
        </div>

        <textarea
          style={styles.textarea}
          placeholder="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <button
          style={styles.primaryBtn}
          onClick={handleCreateInvoice}
          disabled={saving}
        >
          {saving ? "Saving Invoice..." : "Create Invoice"}
        </button>
      </div>

      {showPreview && (
        <div ref={previewRef} style={styles.preview}>
          <h3 style={{ textAlign: "center" }}>INVOICE PREVIEW</h3>

          <p>
            <strong>Invoice No:</strong> {docNumber}
          </p>

          <table style={styles.previewTable}>
            <thead>
              <tr>
                <th style={styles.thSn}>S.No</th>
                <th style={styles.thDesc}>Description</th>
                <th style={styles.thHsn}>HSN</th>
                <th style={styles.thUnit}>Unit</th>
                <th style={styles.thQty}>Qty</th>
                <th style={styles.thRate}>Unit Price</th>
                <th style={styles.thAmount}>Amount</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, i) => (
                <tr key={i}>
                  <td style={styles.tdSn}>{i + 1}</td>
                  <td style={styles.tdDesc}>{item.desc}</td>
                  <td style={styles.tdHsn}>{item.hsn}</td>
                  <td style={styles.tdUnit}>{item.unit}</td>
                  <td style={styles.tdQty}>{item.qty}</td>
                  <td style={styles.tdRate}>{item.rate.toFixed(2)}</td>
                  <td style={styles.tdAmount}>
                    {(item.qty * item.rate).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={styles.previewSummary}>
            <p>Sub Total: ₹ {total.toFixed(2)}</p>
            <p>
              GST ({gstPercent}%): ₹ {gstAmount.toFixed(2)}
            </p>
            <h3>Grand Total: ₹ {grandTotal.toFixed(2)}</h3>
            <p style={styles.amountWords}>
              <strong>Amount in Words: {amountInWords}</strong>
            </p>
          </div>

          <button
            style={styles.primaryBtn}
            onClick={() => setShowSignModal(true)}
          >
            Download PDF
          </button>
        </div>
      )}

      {showSignModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <h3 style={styles.modalTitle}>Include Digital Signature?</h3>

            <label style={styles.radioRow}>
              <input
                type="radio"
                checked={includeSignature}
                onChange={() => setIncludeSignature(true)}
              />
              Use selected company signature
            </label>

            <label style={styles.radioRow}>
              <input
                type="radio"
                checked={!includeSignature}
                onChange={() => setIncludeSignature(false)}
              />
              Generate without signature
            </label>

            <div style={styles.modalActions}>
              <button
                style={styles.secondaryBtn}
                onClick={() => setShowSignModal(false)}
              >
                Cancel
              </button>

              <button
                style={styles.primaryBtn}
                onClick={() => {
                  setShowSignModal(false);
                  generatePDF(includeSignature);
                }}
              >
                Generate PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    padding: "32px",
    background: "linear-gradient(180deg,#f8fafc,#f1f5f9)",
    minHeight: "100vh",
    fontFamily: "Inter, system-ui",
  },
  title: { color: "#2563eb", marginBottom: "16px" },
  backBtn: {
    border: "2px solid #2563eb",
    background: "#e0e7ff",
    color: "#2563eb",
    padding: "8px 14px",
    borderRadius: "10px",
    cursor: "pointer",
    marginBottom: "12px",
  },
  form: {
    background: "#fff",
    padding: "26px",
    borderRadius: "18px",
    boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
  },
  itemsHeader: {
    display: "flex",
    gap: "10px",
    marginBottom: "6px",
    padding: "0 4px",
  },

  itemLabel: {
    flex: 1,
    fontSize: "12px",
    fontWeight: 600,
    color: "#475569",
  },

  label: { fontWeight: 600, margin: "6px", display: "block" },
  input: {
    width: "100%",
    padding: "14px",
    marginBottom: "16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
  },
  textarea: {
    width: "100%",
    padding: "14px",
    marginBottom: "16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontFamily: "sans-serif",
  },
  itemsRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "8px",
    alignItems: "center",
  },
  itemInput: {
    flex: 1,
    padding: "10px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
  },
  deleteBtn: {
    color: "#ef4444",
    fontSize: "25px",
    cursor: "pointer",
  },
  addBtn: {
    background: "#e5e7eb",
    padding: "10px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    marginBottom: "10px",
  },
  primaryBtn: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontWeight: 600,
    cursor: "pointer",
    marginTop: "10px",
  },
  preview: {
    marginTop: "30px",
    background: "#fff",
    padding: "26px",
    borderRadius: "18px",
    boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "12px",
  },
  previewTable: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "16px",
    tableLayout: "fixed", // 🔥 KEY FIX
  },

  /* ---------- HEADERS ---------- */
  thSn: { width: "8%", textAlign: "center", padding: "8px" },
  thDesc: { width: "42%", textAlign: "left", padding: "8px" },
  thQty: { width: "10%", textAlign: "right", padding: "8px" },
  thRate: { width: "15%", textAlign: "right", padding: "8px" },
  thAmount: { width: "15%", textAlign: "right", padding: "8px" },
  thHsn: { width: "10%", textAlign: "center", padding: "8px" },
  thUnit: { width: "10%", textAlign: "center", padding: "8px" },

  tdHsn: { textAlign: "center", padding: "8px" },
  tdUnit: { textAlign: "center", padding: "8px" },

  /* ---------- CELLS ---------- */
  tdSn: { textAlign: "center", padding: "8px" },
  tdDesc: { textAlign: "left", padding: "8px", wordWrap: "break-word" },
  tdQty: { textAlign: "right", padding: "8px" },
  tdRate: { textAlign: "right", padding: "8px" },
  tdAmount: { textAlign: "right", padding: "8px", fontWeight: 600 },
  summaryBox: {
    background: "#f8fafc",
    padding: "16px",
    borderRadius: "12px",
    margin: "16px 0",
    border: "1px dashed #c7d2fe",
  },

  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "6px",
  },

  summaryTotal: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
    fontSize: "16px",
    color: "#1d4ed8",
  },

  amountWords: {
    marginTop: "10px",
    fontStyle: "italic",
    fontSize: "13px",
    color: "#334155",
  },

  previewSummary: {
    marginTop: "14px",
    textAlign: "right",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
  },

  modalBox: {
    background: "#fff",
    padding: "26px",
    borderRadius: "16px",
    width: "360px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },

  modalTitle: {
    marginBottom: "16px",
    color: "#1e293b",
  },

  radioRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "12px",
    fontSize: "14px",
  },

  modalActions: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "20px",
  },

  secondaryBtn: {
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#f1f5f9",
    cursor: "pointer",
    minWidth: "100px",
  },
};

export default CreateInvoice;
